import NFTDisplayer from '@components/NftDisplayer'
import useToast from '@hooks/useToast'
import { Principal } from '@icp-sdk/core/principal'
import { getPrincipals } from '@plug-core/handlers/walletActions/wallet'
import useWallet from '@plug-core/hooks/useWallet'
import { logger } from '@plug-core/logger'
import { useConnectionProvider, useCurrentWallet, useWalletProperties } from '@plug-core/state'
import { Avatar, Button, Heading, IconButton, ListGroup, ListRow, Text, cn } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiMiniPencil } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'

const AVATAR_BACKGROUNDS: Record<string, string> = {
  default: 'bg-bg',
  paper: 'bg-surface',
  mid: 'bg-surface-2',
}

export const AccountAvatar = ({
  avatar,
  size,
  background,
}: {
  avatar: string | undefined
  size: number
  background?: string
}) => {
  const isUrl = avatar?.startsWith('http')

  if (isUrl) {
    return (
      <div
        className="flex shrink-0 overflow-hidden rounded-full"
        style={{ width: size, height: size }}
      >
        <NFTDisplayer nft={{ url: avatar! }} />
      </div>
    )
  }

  return (
    <Avatar
      emoji={avatar || '🚀'}
      size={size}
      className={cn(AVATAR_BACKGROUNDS[background || 'default'] ?? 'bg-bg')}
    />
  )
}

const AccountList = () => {
  const { setCurrentWallet } = useCurrentWallet()
  const { walletNames, walletPlugbookRecords } = useWalletProperties()
  const { wallet } = useWallet()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setToastMessage } = useToast()
  const { provider } = useConnectionProvider()

  const [principals, setPrincipals] = useState<Principal[]>([])

  useEffect(() => {
    getPrincipals()
      .then(setPrincipals)
      .catch((e) => logger.error('Failed to get principals', { originalError: e }))
  }, [])

  const selectWallet = (principal: string) => {
    setCurrentWallet(principal.toString())
    navigate(-1)
  }

  const shareProfile = () => {
    navigator.clipboard.writeText(`https://plugwallet.ooo/users/${wallet.plugbook?.name}`)
    setToastMessage(t('accounts.profileShared'))
  }

  const otherPrincipals = principals.filter((p) => p.toString() !== wallet.principalId)

  return (
    <div className="mb-3 flex flex-col gap-6">
      <div className="mt-6 flex flex-col items-center">
        <AccountAvatar avatar={wallet.plugbook?.profilePic} size={100} background="paper" />

        <Heading level={4} className="mt-5">
          @{wallet.plugbook?.name}
        </Heading>
        <Text variant="body-l" tone="fg-2" className="mt-1.5">
          {wallet.name}
        </Text>

        <div className="mt-4 flex gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate(ROUTES.EDIT_ACCOUNT(wallet.principalId))}
          >
            {t('accounts.editProfile')}
          </Button>

          <Button variant="primary" onClick={shareProfile}>
            {t('accounts.shareProfile')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {otherPrincipals.length > 0 && (
          <>
            <Heading level={5} className="mx-0.5">
              {t('accounts.otherAccounts')}
            </Heading>

            <ListGroup>
              {otherPrincipals.map((principal) => (
                <div
                  key={principal.toString()}
                  role="listitem"
                  className="cursor-pointer transition-colors duration-150 hover:bg-surface-2"
                  onClick={() => selectWallet(principal.toString())}
                >
                  <ListRow
                    role="presentation"
                    leading={
                      <AccountAvatar
                        avatar={walletPlugbookRecords[principal.toString()]?.profilePic}
                        size={40}
                      />
                    }
                    primary={'@' + walletPlugbookRecords[principal.toString()]?.name}
                    secondary={walletNames[principal.toString()]}
                    trailing={
                      <IconButton
                        aria-label={t('accounts.editProfile')}
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(ROUTES.EDIT_ACCOUNT(principal.toString()))
                        }}
                      >
                        <HiMiniPencil />
                      </IconButton>
                    }
                  />
                </div>
              ))}
            </ListGroup>
          </>
        )}

        {!provider && (
          <Button
            onClick={() => navigate(ROUTES.WALLET_IMPORT_TYPE)}
            variant="secondary"
            fullWidth
            className="mt-2"
          >
            {t('accounts.addNew')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default AccountList
